import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Save, Key } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../../firebase';
import { sanitizeForFirestore } from '../../utils/firestoreUtils';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import RoleBadge from '../../components/RoleBadge';
import CallingSettings from './CallingSettings';
import { toast } from 'sonner';

const MyAccount = () => {
  const { user: currentUser, isAuthLoading, setUser } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const [dataLoading, setDataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Form state - only editable fields
  const [displayName, setDisplayName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [initialDisplayName, setInitialDisplayName] = useState('');
  const [initialPhotoURL, setInitialPhotoURL] = useState('');

  useEffect(() => {
    const loadUserData = async () => {
      // Wait for auth to initialize
      if (isAuthLoading || roleLoading) {
        return;
      }

      // If no user after auth loads, don't try to load data
      if (!currentUser) {
        return;
      }

      setDataLoading(true);

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          // Set form data with defaults for missing fields
          const name = data.displayName || currentUser.displayName || '';
          const photo = data.photoURL || currentUser.photoURL || '';
          
          setDisplayName(name);
          setPhotoURL(photo);
          setInitialDisplayName(name);
          setInitialPhotoURL(photo);
        } else {
          // Document doesn't exist, use Firebase Auth data
          const name = currentUser.displayName || '';
          const photo = currentUser.photoURL || '';
          
          setDisplayName(name);
          setPhotoURL(photo);
          setInitialDisplayName(name);
          setInitialPhotoURL(photo);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        toast.error('Failed to load profile data');
        // Set defaults even on error
        setDisplayName('');
        setPhotoURL('');
        setInitialDisplayName('');
        setInitialPhotoURL('');
      } finally {
        setDataLoading(false);
      }
    };

    loadUserData();
  }, [currentUser, isAuthLoading, roleLoading]);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const handleSaveChanges = async () => {
    if (!currentUser) return;

    // Check if anything actually changed
    const displayNameChanged = displayName !== initialDisplayName;
    const photoChanged = profileImage !== null;

    if (!displayNameChanged && !photoChanged) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    setUploading(false);

    try {
      let newPhotoURL = photoURL;

      // Upload profile image if selected
      if (profileImage) {
        setUploading(true);
        const storageRef = ref(storage, `profile-images/${currentUser.uid}`);
        const uploadResult = await uploadBytes(storageRef, profileImage);
        newPhotoURL = await getDownloadURL(uploadResult.ref);
        setUploading(false);
      }

      // Update Firestore user document - ONLY allowed fields
      const userDocRef = doc(db, 'users', currentUser.uid);
      const updateData = sanitizeForFirestore({
        displayName: displayName,
        photoURL: newPhotoURL,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(userDocRef, updateData);

      // Update Firebase Auth profile to keep it in sync
      await updateProfile(currentUser, {
        displayName: displayName,
        photoURL: newPhotoURL,
      });

      // Reload auth user to get fresh data
      await currentUser.reload();

      // Trigger AuthContext refresh
      if (setUser) {
        setUser({ ...auth.currentUser });
      }

      // Update local state with new values
      setPhotoURL(newPhotoURL);
      setInitialDisplayName(displayName);
      setInitialPhotoURL(newPhotoURL);
      setProfileImage(null);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile changes');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!currentUser || !currentUser.email) return;

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      toast.success(`Password reset email sent to ${currentUser.email}`);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  // Show loading spinner while auth is initializing
  if (isAuthLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Only show "Please log in" after auth has finished initializing and user is still null
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Please log in to view your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <User className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-secondary">My Account</h2>
          <p className="text-sm text-muted-foreground">
            Manage your personal account settings and profile information.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Profile Photo */}
        <div className="space-y-2">
          <Label>Profile Photo</Label>
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
              {photoURL || profileImage ? (
                <img
                  src={
                    profileImage
                      ? URL.createObjectURL(profileImage)
                      : photoURL
                  }
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <User className="h-10 w-10 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={saving || uploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: Square image, at least 400x400px
              </p>
            </div>
          </div>
        </div>

        {/* Display Name */}
        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
            disabled={saving || uploading}
          />
        </div>

        {/* Email (Read-only) */}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <Input
              id="email"
              value={currentUser?.email || ''}
              disabled
              className="bg-gray-50 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-gray-500">Email cannot be changed</p>
        </div>

        {/* Role (Read-only) */}
        <div className="space-y-2">
          <Label>Role</Label>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-gray-400" />
            <RoleBadge role={role} />
          </div>
          <p className="text-xs text-gray-500">Role is managed by administrators</p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <Button
            onClick={handleSaveChanges}
            disabled={saving || uploading}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
          >
            {saving || uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {uploading ? 'Uploading...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>

          <Button onClick={handlePasswordReset} variant="outline">
            <Key className="h-4 w-4 mr-2" />
            Reset Password
          </Button>
        </div>
      </div>

      {/* Calling — direct-first outbound call method. */}
      <CallingSettings />
    </div>
  );
};

export default MyAccount;
